import React, { useState, useEffect } from 'react'
import {
  Card,
  Steps,
  Button,
  Form,
  InputNumber,
  Select,
  Table,
  Space,
  Alert,
  Tag,
  Row,
  Col,
  Statistic,
  message,
  Modal,
  DatePicker,
  Input,
  Divider,
  Typography,
  Tooltip,
  Progress,
  Badge
} from 'antd'
import {
  DollarOutlined,
  CheckCircleOutlined,
  WarningOutlined,
  InfoCircleOutlined,
  CalculatorOutlined,
  FileTextOutlined
} from '@ant-design/icons'
import dayjs from 'dayjs'
import { formatCurrency } from '../../utils/helpers'
import { createLedgerTransaction, getPatientLedgerSummary, recordPayment } from '../../backend-integration/api'
import { getInvoicesWithBillingRecords } from '../../../Invoices/utils'
import { TRANSACTION_TYPES } from '../../utils/enums'
import store from '../../../../store'

const { Step } = Steps
const { Option } = Select
const { Title, Text } = Typography
const { TextArea } = Input

const PaymentAllocationWizard = ({ visible, onClose, patientId, accountData, preselectedPayment = null }) => {
  const [currentStep, setCurrentStep] = useState(0)
  const [loading, setLoading] = useState(false)
  const [form] = Form.useForm()

  // Payment details
  const [paymentDetails, setPaymentDetails] = useState({
    amount: 0,
    paymentMethod: 'cash',
    paymentDate: dayjs(),
    reference: '',
    payer: 'patient', // patient or medical_aid
    description: ''
  })

  // Invoices and allocations
  const [invoices, setInvoices] = useState([])
  const [allocations, setAllocations] = useState({})
  const [remainingAmount, setRemainingAmount] = useState(0)
  const [autoAllocated, setAutoAllocated] = useState(false)

  // Summary
  const [allocationSummary, setAllocationSummary] = useState(null)

  useEffect(() => {
    if (visible && patientId) {
      loadPatientInvoices()
      if (preselectedPayment) {
        setPaymentDetails({
          ...paymentDetails,
          ...preselectedPayment
        })
        form.setFieldsValue(preselectedPayment)
      }
    }
  }, [visible, patientId])

  useEffect(() => {
    calculateRemainingAmount()
  }, [allocations, paymentDetails.amount])

  const loadPatientInvoices = async () => {
    setLoading(true)
    try {
      // Get unpaid invoices for the patient with billing records
      const allInvoices = await getInvoicesWithBillingRecords()
      let invoicesData = allInvoices
        .filter((inv) => inv.patientId === patientId && ['unpaid', 'partial'].includes(inv.status))
        .map((inv) => ({
          ...inv,
          medicalAidBalance: inv.billingRecord?.medicalAidDue || inv.billingRecord?.medicalAid || 0,
          patientBalance: inv.billingRecord?.patientDue || inv.billingRecord?.patient || 0
        }))

      // Filter out private invoices if this is a medical aid payment
      if (paymentDetails.payer === 'medical_aid') {
        invoicesData = invoicesData.filter((inv) => inv.accountGroup !== 'private')
        console.log('Filtered out private invoices for medical aid payment. Remaining invoices:', invoicesData.length)
      }

      // Sort by date (oldest first for FIFO)
      const sortedInvoices = invoicesData.sort((a, b) => new Date(a.invoiceDate) - new Date(b.invoiceDate))

      setInvoices(sortedInvoices)
    } catch (error) {
      console.error('Failed to load invoices:', error)
      message.error('Failed to load patient invoices')
    } finally {
      setLoading(false)
    }
  }

  const calculateRemainingAmount = () => {
    const totalAllocated = Object.values(allocations).reduce((sum, amount) => sum + (amount || 0), 0)
    setRemainingAmount(paymentDetails.amount - totalAllocated)
  }

  const handleAutoAllocate = () => {
    const newAllocations = {}
    let remainingPayment = paymentDetails.amount

    // FIFO allocation
    for (const invoice of invoices) {
      if (remainingPayment <= 0) break

      const outstandingAmount =
        paymentDetails.payer === 'medical_aid' ? invoice.medicalAidBalance || 0 : invoice.patientBalance || 0

      if (outstandingAmount > 0) {
        const allocationAmount = Math.min(remainingPayment, outstandingAmount)
        newAllocations[invoice._id] = allocationAmount
        remainingPayment -= allocationAmount
      }
    }

    setAllocations(newAllocations)
    setAutoAllocated(true)
    message.success('Payment auto-allocated using FIFO method')
  }

  const handleAllocationChange = (invoiceId, value) => {
    const invoice = invoices.find((inv) => inv._id === invoiceId)

    // Prevent medical aid payments to private invoices
    if (paymentDetails.payer === 'medical_aid' && invoice.accountGroup === 'private') {
      message.error('Medical aid payments cannot be allocated to private invoices')
      return
    }

    const maxAmount = paymentDetails.payer === 'medical_aid' ? invoice.medicalAidBalance : invoice.patientBalance

    // Prevent over-allocation on individual invoice
    if (value > maxAmount) {
      message.warning(`Cannot allocate more than ${formatCurrency(maxAmount)} to this invoice`)
      return
    }

    // Check if this would exceed total payment
    const otherAllocations = Object.entries(allocations)
      .filter(([id]) => id !== invoiceId)
      .reduce((sum, [_, amount]) => sum + amount, 0)

    if (otherAllocations + value > paymentDetails.amount) {
      message.warning('This allocation would exceed the total payment amount')
      return
    }

    setAllocations({
      ...allocations,
      [invoiceId]: value || 0
    })
  }

  const validateAllocation = () => {
    const totalAllocated = Object.values(allocations).reduce((sum, amount) => sum + amount, 0)

    if (totalAllocated === 0) {
      message.error('Please allocate the payment to at least one invoice')
      return false
    }

    if (totalAllocated > paymentDetails.amount) {
      message.error('Total allocation exceeds payment amount')
      return false
    }

    if (remainingAmount > 0) {
      return new Promise((resolve) => {
        Modal.confirm({
          title: 'Unallocated Amount',
          content: `There is ${formatCurrency(
            remainingAmount
          )} unallocated. This will be recorded as a credit on the account. Continue?`,
          onOk: () => resolve(true),
          onCancel: () => resolve(false)
        })
      })
    }

    return true
  }

  const handleNext = async () => {
    if (currentStep === 0) {
      // Validate payment details
      try {
        const values = await form.validateFields()

        // Check for potential overpayment issues before proceeding
        const paymentAmount = parseFloat(values.amount) || 0
        const totalPatientBalance = invoices.reduce((sum, inv) => sum + (inv.patientBalance || 0), 0)
        const totalMedicalAidBalance = invoices.reduce((sum, inv) => sum + (inv.medicalAidBalance || 0), 0)

        if (values.payer === 'patient' && paymentAmount > totalPatientBalance && totalMedicalAidBalance > 0) {
          const proceed = await new Promise((resolve) => {
            Modal.confirm({
              title: 'Payment Amount Warning',
              content: (
                <div>
                  <p>
                    ⚠️ <strong>Potential Overpayment Detected</strong>
                  </p>
                  <p>
                    Payment amount: <strong>R{paymentAmount.toFixed(2)}</strong>
                  </p>
                  <p>
                    Patient balance: <strong>R{totalPatientBalance.toFixed(2)}</strong>
                  </p>
                  <p>
                    Medical aid balance: <strong>R{totalMedicalAidBalance.toFixed(2)}</strong>
                  </p>
                  <br />
                  <p>
                    This payment exceeds the patient balance while medical aid amounts are still outstanding. Consider
                    allocating part of this payment to medical aid invoices.
                  </p>
                </div>
              ),
              okText: 'Continue Anyway',
              cancelText: 'Review Payment',
              onOk: () => resolve(true),
              onCancel: () => resolve(false)
            })
          })

          if (!proceed) return
        }

        if (values.payer === 'medical_aid' && paymentAmount > totalMedicalAidBalance && totalPatientBalance > 0) {
          const proceed = await new Promise((resolve) => {
            Modal.confirm({
              title: 'Payment Amount Warning',
              content: (
                <div>
                  <p>
                    ⚠️ <strong>Potential Overpayment Detected</strong>
                  </p>
                  <p>
                    Payment amount: <strong>R{paymentAmount.toFixed(2)}</strong>
                  </p>
                  <p>
                    Medical aid balance: <strong>R{totalMedicalAidBalance.toFixed(2)}</strong>
                  </p>
                  <p>
                    Patient balance: <strong>R{totalPatientBalance.toFixed(2)}</strong>
                  </p>
                  <br />
                  <p>This payment exceeds the medical aid balance while patient amounts are still outstanding.</p>
                </div>
              ),
              okText: 'Continue Anyway',
              cancelText: 'Review Payment',
              onOk: () => resolve(true),
              onCancel: () => resolve(false)
            })
          })

          if (!proceed) return
        }

        setPaymentDetails(values)
        setCurrentStep(1)
      } catch (error) {
        console.error('Validation failed:', error)
      }
    } else if (currentStep === 1) {
      // Validate allocations
      const isValid = await validateAllocation()
      if (isValid) {
        generateAllocationSummary()
        setCurrentStep(2)
      }
    } else if (currentStep === 2) {
      // Process payment
      await processPaymentAllocation()
    }
  }

  const generateAllocationSummary = () => {
    const allocatedInvoices = invoices
      .filter((inv) => allocations[inv._id] > 0)
      .map((inv) => ({
        ...inv,
        allocatedAmount: allocations[inv._id],
        remainingBalance:
          paymentDetails.payer === 'medical_aid'
            ? inv.medicalAidBalance - allocations[inv._id]
            : inv.patientBalance - allocations[inv._id]
      }))

    setAllocationSummary({
      payment: paymentDetails,
      allocations: allocatedInvoices,
      totalAllocated: Object.values(allocations).reduce((sum, amount) => sum + amount, 0),
      unallocatedAmount: remainingAmount
    })
  }

  const processPaymentAllocation = async () => {
    setLoading(true)
    try {
      const entityRef = store.getState().core.entity._id
      const userId = store.getState().auth.agiliteUser?._id

      // 1. Create payment transaction
      const billingId = accountData?.billingId
      const paymentTransaction = await createLedgerTransaction({
        patientId,
        billingId: billingId,
        // Use billingId for accountNo field
        type:
          paymentDetails.payer === 'medical_aid' ? TRANSACTION_TYPES.MEDICAL_AID_PAYMENT : TRANSACTION_TYPES.PAYMENT,
        debit: 0,
        credit: paymentDetails.amount,
        balance: 0, // Will be calculated by backend
        description: `Payment received - ${paymentDetails.paymentMethod.toUpperCase()} ${
          paymentDetails.reference ? `(Ref: ${paymentDetails.reference})` : ''
        }`,
        reference: paymentDetails.reference,
        date: paymentDetails.paymentDate.toISOString(),
        entityRef,
        createdBy: userId
      })

      // 2. Create allocation records
      const allocationPromises = Object.entries(allocations)
        .filter(([_, amount]) => amount > 0)
        .map(([invoiceId, amount]) => {
          const invoice = invoices.find((inv) => inv._id === invoiceId)
          // For now, we'll just track this in the transaction description
          // In production, this would be a separate allocation record
          return Promise.resolve({
            paymentId: paymentTransaction._id,
            invoiceId,
            invoiceNo: invoice.invoiceNo,
            patientId,
            amount,
            allocationType: paymentDetails.payer,
            allocationDate: new Date(),
            entityRef,
            createdBy: userId
          })
        })

      await Promise.all(allocationPromises)

      // 3. Handle unallocated amount if any
      if (remainingAmount > 0) {
        await createLedgerTransaction({
          patientId,
          billingId: billingId,
          // Use billingId for accountNo field
          type: TRANSACTION_TYPES.CREDIT,
          debit: 0,
          credit: remainingAmount,
          description: 'Unallocated payment credit',
          reference: paymentDetails.reference,
          date: paymentDetails.paymentDate.toISOString(),
          entityRef,
          createdBy: userId
        })
      }

      message.success('Payment allocated successfully')
      onClose(true) // Pass true to indicate successful allocation
    } catch (error) {
      console.error('Failed to process payment allocation:', error)
      message.error('Failed to process payment allocation')
    } finally {
      setLoading(false)
    }
  }

  const columns = [
    {
      title: 'Invoice No',
      dataIndex: 'invoiceNo',
      key: 'invoiceNo',
      width: 120,
      render: (text) => <Tag color='blue'>{text}</Tag>
    },
    {
      title: 'Date',
      dataIndex: 'invoiceDate',
      key: 'invoiceDate',
      width: 100,
      render: (date) => dayjs(date).format('DD MMM YY')
    },
    {
      title: 'Total Amount',
      dataIndex: 'totalAmount',
      key: 'totalAmount',
      width: 120,
      align: 'right',
      render: (amount) => formatCurrency(amount)
    },
    {
      title: paymentDetails.payer === 'medical_aid' ? 'Medical Aid Due' : 'Patient Due',
      key: 'outstanding',
      width: 120,
      align: 'right',
      render: (_, record) => {
        const amount = paymentDetails.payer === 'medical_aid' ? record.medicalAidBalance : record.patientBalance
        return (
          <Text strong style={{ color: '#ff4d4f' }}>
            {formatCurrency(amount)}
          </Text>
        )
      }
    },
    {
      title: 'Allocate Amount',
      key: 'allocate',
      width: 150,
      render: (_, record) => {
        const maxAmount = paymentDetails.payer === 'medical_aid' ? record.medicalAidBalance : record.patientBalance

        return (
          <InputNumber
            min={0}
            max={maxAmount}
            value={allocations[record._id] || 0}
            onChange={(value) => handleAllocationChange(record._id, value)}
            formatter={(value) => `R ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
            parser={(value) => value.replace(/R\s?|(,*)/g, '')}
            style={{ width: '100%' }}
            disabled={maxAmount === 0}
          />
        )
      }
    },
    {
      title: 'Status',
      key: 'status',
      width: 100,
      render: (_, record) => {
        const allocated = allocations[record._id] || 0
        const outstanding = paymentDetails.payer === 'medical_aid' ? record.medicalAidBalance : record.patientBalance

        if (allocated === 0) return <Tag>Unallocated</Tag>
        if (allocated >= outstanding) return <Tag color='green'>Fully Allocated</Tag>
        return <Tag color='orange'>Partial</Tag>
      }
    }
  ]

  const steps = [
    {
      title: 'Payment Details',
      icon: <DollarOutlined />
    },
    {
      title: 'Allocate to Invoices',
      icon: <CalculatorOutlined />
    },
    {
      title: 'Review & Confirm',
      icon: <CheckCircleOutlined />
    }
  ]

  return (
    <Modal
      title='Payment Allocation Wizard'
      visible={visible}
      onCancel={() => onClose(false)}
      width={900}
      footer={null}
      destroyOnClose
    >
      <Steps current={currentStep} style={{ marginBottom: 24 }}>
        {steps.map((item) => (
          <Step key={item.title} title={item.title} icon={item.icon} />
        ))}
      </Steps>

      {currentStep === 0 && (
        <Form
          form={form}
          layout='vertical'
          initialValues={paymentDetails}
          onValuesChange={(_, values) => setPaymentDetails({ ...paymentDetails, ...values })}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name='amount'
                label='Payment Amount'
                rules={[
                  { required: true, message: 'Please enter payment amount' },
                  { type: 'number', min: 0.01, message: 'Amount must be greater than 0' }
                ]}
              >
                <InputNumber
                  prefix='R'
                  style={{ width: '100%' }}
                  formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name='paymentDate'
                label='Payment Date'
                rules={[{ required: true, message: 'Please select payment date' }]}
              >
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name='paymentMethod'
                label='Payment Method'
                rules={[{ required: true, message: 'Please select payment method' }]}
              >
                <Select>
                  <Option value='cash'>Cash</Option>
                  <Option value='eft'>EFT</Option>
                  <Option value='card'>Card</Option>
                  <Option value='cheque'>Cheque</Option>
                  <Option value='medical_aid'>Medical Aid</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name='payer' label='Payer' rules={[{ required: true, message: 'Please select payer' }]}>
                <Select>
                  <Option value='patient'>Patient</Option>
                  <Option value='medical_aid'>Medical Aid</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name='reference'
            label='Payment Reference'
            rules={[
              ({ getFieldValue }) => ({
                validator(_, value) {
                  const paymentMethod = getFieldValue('paymentMethod')
                  if (paymentMethod === 'eft' && (!value || value.trim().length === 0)) {
                    return Promise.reject(new Error('Reference number is required for EFT payments'))
                  }
                  return Promise.resolve()
                }
              })
            ]}
          >
            <Input placeholder='e.g., Receipt number, transaction ID' />
          </Form.Item>

          <Form.Item name='description' label='Description/Notes'>
            <TextArea rows={3} placeholder='Additional notes about this payment' />
          </Form.Item>
        </Form>
      )}

      {currentStep === 1 && (
        <div>
          <Alert
            message='Allocate Payment to Invoices'
            description={`Allocate the ${formatCurrency(
              paymentDetails.amount
            )} payment to outstanding invoices. The system will prevent over-allocation.`}
            type='info'
            showIcon
            style={{ marginBottom: 16 }}
          />

          <Row gutter={16} style={{ marginBottom: 16 }}>
            <Col span={8}>
              <Card>
                <Statistic title='Payment Amount' value={paymentDetails.amount} precision={2} prefix='R' />
              </Card>
            </Col>
            <Col span={8}>
              <Card>
                <Statistic
                  title='Allocated'
                  value={Object.values(allocations).reduce((sum, amt) => sum + amt, 0)}
                  precision={2}
                  prefix='R'
                  valueStyle={{ color: '#52c41a' }}
                />
              </Card>
            </Col>
            <Col span={8}>
              <Card>
                <Statistic
                  title='Remaining'
                  value={remainingAmount}
                  precision={2}
                  prefix='R'
                  valueStyle={{ color: remainingAmount > 0 ? '#fa8c16' : '#52c41a' }}
                />
              </Card>
            </Col>
          </Row>

          <Space style={{ marginBottom: 16 }}>
            <Button icon={<CalculatorOutlined />} onClick={handleAutoAllocate}>
              Auto-Allocate (FIFO)
            </Button>
            <Button
              onClick={() => {
                setAllocations({})
                setAutoAllocated(false)
              }}
            >
              Clear All
            </Button>
          </Space>

          <Table
            columns={columns}
            dataSource={invoices}
            loading={loading}
            pagination={false}
            scroll={{ y: 300 }}
            rowKey='_id'
            summary={() => (
              <Table.Summary fixed>
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0} colSpan={4}>
                    <Text strong>Total</Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={1} align='right'>
                    <Text strong style={{ color: '#52c41a' }}>
                      {formatCurrency(Object.values(allocations).reduce((sum, amt) => sum + amt, 0))}
                    </Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={2} />
                </Table.Summary.Row>
              </Table.Summary>
            )}
          />
        </div>
      )}

      {currentStep === 2 && allocationSummary && (
        <div>
          <Alert
            message='Review Payment Allocation'
            description='Please review the allocation details before confirming'
            type='success'
            showIcon
            style={{ marginBottom: 16 }}
          />

          <Card title='Payment Summary' style={{ marginBottom: 16 }}>
            <Row gutter={16}>
              <Col span={12}>
                <Text type='secondary'>Amount:</Text>
                <br />
                <Text strong style={{ fontSize: 18 }}>
                  {formatCurrency(allocationSummary.payment.amount)}
                </Text>
              </Col>
              <Col span={12}>
                <Text type='secondary'>Payment Method:</Text>
                <br />
                <Text strong>{allocationSummary.payment.paymentMethod.toUpperCase()}</Text>
              </Col>
            </Row>
            <Divider />
            <Row gutter={16}>
              <Col span={12}>
                <Text type='secondary'>Reference:</Text>
                <br />
                <Text>{allocationSummary.payment.reference || 'N/A'}</Text>
              </Col>
              <Col span={12}>
                <Text type='secondary'>Date:</Text>
                <br />
                <Text>{allocationSummary.payment.paymentDate.format('DD MMM YYYY')}</Text>
              </Col>
            </Row>
          </Card>

          <Card title='Allocation Details'>
            {allocationSummary.allocations.map((invoice) => (
              <div key={invoice._id} style={{ marginBottom: 16 }}>
                <Row justify='space-between' align='middle'>
                  <Col span={12}>
                    <Text strong>Invoice {invoice.invoiceNo}</Text>
                    <br />
                    <Text type='secondary' style={{ fontSize: 12 }}>
                      {dayjs(invoice.invoiceDate).format('DD MMM YYYY')}
                    </Text>
                  </Col>
                  <Col span={12} style={{ textAlign: 'right' }}>
                    <Text>
                      Outstanding:{' '}
                      {formatCurrency(
                        allocationSummary.payment.payer === 'medical_aid'
                          ? invoice.medicalAidBalance
                          : invoice.patientBalance
                      )}
                    </Text>
                    <br />
                    <Text strong style={{ color: '#52c41a' }}>
                      Allocated: {formatCurrency(invoice.allocatedAmount)}
                    </Text>
                    <br />
                    <Text type='secondary'>Remaining: {formatCurrency(invoice.remainingBalance)}</Text>
                  </Col>
                </Row>
                <Progress
                  percent={Math.round(
                    (invoice.allocatedAmount /
                      (allocationSummary.payment.payer === 'medical_aid'
                        ? invoice.medicalAidBalance
                        : invoice.patientBalance)) *
                      100
                  )}
                  strokeColor='#52c41a'
                  style={{ marginTop: 8 }}
                />
              </div>
            ))}

            {allocationSummary.unallocatedAmount > 0 && (
              <Alert
                message={`Unallocated Amount: ${formatCurrency(allocationSummary.unallocatedAmount)}`}
                description="This amount will be recorded as a credit on the patient's account"
                type='warning'
                showIcon
                style={{ marginTop: 16 }}
              />
            )}
          </Card>
        </div>
      )}

      <div style={{ marginTop: 24, textAlign: 'right' }}>
        <Space>
          {currentStep > 0 && <Button onClick={() => setCurrentStep(currentStep - 1)}>Previous</Button>}
          <Button type='primary' loading={loading} onClick={handleNext}>
            {currentStep === 2 ? 'Confirm & Process' : 'Next'}
          </Button>
        </Space>
      </div>
    </Modal>
  )
}

export default PaymentAllocationWizard
